import React, { useState, useEffect } from "react";
import { api } from "../services/api";

const Settings = () => {
  const [settings, setSettings] = useState({
    sandboxMode: true,
    clientId: "",
    secretKey: "",
    commission: 10
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);

  // Fetch settings on component mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/settings");
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'number' && name === 'commission') {
      // Allow empty string for partial input while typing
      if (value === '') {
        setSettings(prev => ({ ...prev, [name]: '' }));
        return;
      }
      
      const numValue = parseFloat(value);
      // Allow any numeric input while typing, validation happens on blur/save
      if (!isNaN(numValue)) {
        setSettings(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setSettings(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value
      }));
    }
  };

  const handleCommissionBlur = (e) => {
    const value = e.target.value;
    const numValue = parseFloat(value);
    
    // If empty or invalid, set to minimum (1)
    if (value === '' || isNaN(numValue) || numValue < 1) {
      setSettings(prev => ({ ...prev, commission: 1 }));
    } 
    // If exceeds maximum, set to maximum (100)
    else if (numValue > 100) {
      setSettings(prev => ({ ...prev, commission: 100 }));
    }
    // Otherwise, ensure it's a valid number
    else {
      setSettings(prev => ({ ...prev, commission: numValue }));
    }
  };

  const handleSave = async () => {
    // Validate commission
    const commission = parseFloat(settings.commission);
    if (isNaN(commission) || commission < 1 || commission > 100) {
      alert("Commission must be between 1 and 100");
      return;
    }

    try {
      setSaving(true);
      await api.post("/api/settings", { ...settings, commission });
      alert("Settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Failed to save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

    if (loading) {
    return (
      <div className="ml-0 mt-5">
        <div className="px-5 pt-1">
          <h1 className="text-3xl font-bold text-white mb-8">Settings</h1>
          <div className="card-bg p-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-600 rounded w-1/4 mb-4"></div>
              <div className="space-y-3">
                <div className="h-10 bg-gray-600 rounded"></div>
                <div className="h-10 bg-gray-600 rounded"></div>
                <div className="h-10 bg-gray-600 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ml-0 mt-5">
      <div className="px-5 pt-1">
        <h1 className="text-3xl font-bold text-white mb-8">Settings</h1>

        {/* PayPal Payment Settings */}
        <div className="card-bg p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow" fill="currentColor" viewBox="0 0 24 24">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.543-.676c-.608-.624-1.47-1.02-2.53-1.02H7.12c-.524 0-.968.382-1.05.9L4.944 18.16h4.632l1.484-9.4h.005c.524 0 .968-.382 1.05-.9l.12-.76c.08-.524.526-.9 1.05-.9h.66c2.29 0 4.04.543 5.09 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106h4.632l1.484-9.4h.005c.524 0 .968-.382 1.05-.9l.12-.76c.08-.524.526-.9 1.05-.9h.66c2.29 0 4.04.543 5.09 1.81z"/>
              </svg>
              PayPal Payment Settings
            </h2>

            {/* Mode Toggle */}
            <div className="flex items-center bg-secondary-light rounded-lg p-1">
              <button
                onClick={() => setSettings(prev => ({ ...prev, sandboxMode: true }))}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  settings.sandboxMode
                    ? "bg-yellow text-black"
                    : "text-text-light-gray hover:text-white"
                }`}
              >
                Sandbox
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, sandboxMode: false }))}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  !settings.sandboxMode
                    ? "bg-yellow text-black"
                    : "text-text-light-gray hover:text-white"
                }`}
              >
                Live
              </button>
            </div>
          </div>

          {/* PayPal Credentials */}
          <div>
            <h3 className="text-lg font-medium text-white mb-4 border-b border-accent-dark pb-2">
              {settings.sandboxMode ? "Sandbox Credentials" : "Live Credentials (Production)"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  PayPal Client ID
                </label>
                <input
                  type="text"
                  name="clientId"
                  value={settings.clientId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-secondary-light border border-accent-dark rounded-md text-white placeholder-text-light-gray focus:outline-none focus:ring-2 focus:ring-yellow focus:border-transparent"
                  placeholder="Enter your PayPal Client ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  PayPal Secret Key
                </label>
                <div className="relative">
                  <input
                    type={showSecretKey ? "text" : "password"}
                    name="secretKey"
                    value={settings.secretKey}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 pr-10 bg-secondary-light border border-accent-dark rounded-md text-white placeholder-text-light-gray focus:outline-none focus:ring-2 focus:ring-yellow focus:border-transparent"
                    placeholder="Enter your PayPal Secret Key"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecretKey(!showSecretKey)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-light-gray hover:text-white transition-colors focus:outline-none"
                    aria-label={showSecretKey ? "Hide secret key" : "Show secret key"}
                  >
                    {showSecretKey ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Platform Commission Settings */}
        <div className="card-bg p-6 mb-8">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
            <svg className="w-6 h-6 mr-2 text-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Platform Commission
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Commission (%)
              </label>
              <input
                type="number"
                name="commission"
                value={settings.commission}
                onChange={handleInputChange}
                onBlur={handleCommissionBlur}
                className="w-full px-3 py-2 bg-secondary-light border border-accent-dark rounded-md text-white placeholder-text-light-gray focus:outline-none focus:ring-2 focus:ring-yellow focus:border-transparent"
                placeholder="Enter commission percentage (1-100)"
                min="1"
                max="100"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end py-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-yellow hover:bg-yellow-600 disabled:bg-gray-500 disabled:cursor-not-allowed text-black font-medium py-2 px-6 rounded-md transition duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-yellow focus:ring-offset-2 focus:ring-offset-secondary"
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;